import { collection, getDocs, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import * as XLSX from 'xlsx';

/**
 * AI Timetable Service
 * Provides functions for AI chatbot to design and create timetables
 * using teacher data from /school-users/{tenantId}/teachers
 */

export interface Teacher {
  teacherId: string;
  name: string;
  subject: string;
  subjects?: string[]; // Multiple subjects a teacher can teach
  department?: string;
  email?: string;
  phone?: string;
  qualification?: string;
  maxPeriodsPerDay?: number;
  availability?: {
    [day: string]: { start: string; end: string } | boolean;
  };
}

export interface Period {
  periodId: string;
  periodNumber: string;
  order: number;
  subject: string;
  teacherId: string;
  teacherName: string;
  startTime: string;
  endTime: string;
  duration: number;
  isBreak: boolean;
  type: string;
}

export interface TimetableConfig {
  classId: string;
  className: string;
  section: string;
  day: string;
  academicYear: string;
  periods: Period[];
  docId?: string; // Firestore document ID for updates
}

export interface TimetableGenerationRequest {
  classId: string;
  section: string;
  day: string;
  subjects: string[];
  periodTimings: { startTime: string; endTime: string; isBreak?: boolean }[];
  academicYear?: string;
}

// Fetch all teachers with their details
export const fetchTeachersForTimetable = async (domain: string): Promise<Teacher[]> => {
  try {
    console.log('🔍 Fetching teachers for timetable design, domain:', domain);
    const teachersRef = collection(db, `school-users/${domain}/teachers`);
    const snapshot = await getDocs(teachersRef);

    const teachers: Teacher[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      teachers.push({
        teacherId: data.teacherId || doc.id,
        name: data.name || '',
        subject: data.subject || '',
        subjects: data.subjects || [data.subject].filter(Boolean),
        department: data.department || '',
        email: data.email || '',
        phone: data.phone || '',
        qualification: data.qualification || '',
        maxPeriodsPerDay: data.maxPeriodsPerDay || 6,
        availability: data.availability || {},
      });
    });

    console.log(`📊 Found ${teachers.length} teachers`);
    return teachers.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('❌ Error fetching teachers:', error);
    return [];
  }
};

// Get teachers by subject
export const getTeachersBySubject = async (domain: string, subject: string): Promise<Teacher[]> => {
  const teachers = await fetchTeachersForTimetable(domain);
  return teachers.filter(t =>
    t.subject?.toLowerCase().includes(subject.toLowerCase()) ||
    t.subjects?.some(s => s.toLowerCase().includes(subject.toLowerCase()))
  );
};

// Get teacher's existing schedule for a day (to check availability)
export const getTeacherScheduleForDay = async (
  domain: string,
  teacherId: string,
  day: string
): Promise<Period[]> => {
  try {
    const timetablesRef = collection(db, `school-users/${domain}/timetables`);
    const snapshot = await getDocs(timetablesRef);

    const teacherPeriods: Period[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.day?.toLowerCase() === day.toLowerCase() && data.periods) {
        data.periods.forEach((period: Period) => {
          if (period.teacherId === teacherId) {
            teacherPeriods.push({
              ...period,
              // Add class info for context
              subject: `${period.subject} (${data.className})`,
            });
          }
        });
      }
    });

    return teacherPeriods.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.error('Error fetching teacher schedule:', error);
    return [];
  }
};

// Interface for teacher availability info
export interface TeacherAvailabilityInfo {
  teacherId: string;
  teacherName: string;
  subject: string;
  busyPeriods: {
    time: string;
    className: string;
    subject: string;
  }[];
  availablePeriods: string[];
}

// Get all timetables for a specific day across all classes
export const getAllTimetablesForDay = async (
  domain: string,
  day: string
): Promise<{ className: string; periods: Period[] }[]> => {
  try {
    const timetablesRef = collection(db, `school-users/${domain}/timetables`);
    const snapshot = await getDocs(timetablesRef);

    const dayTimetables: { className: string; periods: Period[] }[] = [];

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.day?.toLowerCase() === day.toLowerCase() && data.periods) {
        dayTimetables.push({
          className: data.className || data.classId || 'Unknown',
          periods: data.periods,
        });
      }
    });

    return dayTimetables;
  } catch (error) {
    console.error('Error fetching timetables for day:', error);
    return [];
  }
};

// Build comprehensive teacher availability map for a day
export const buildTeacherAvailabilityMap = async (
  domain: string,
  day: string
): Promise<Map<string, TeacherAvailabilityInfo>> => {
  const teachers = await fetchTeachersForTimetable(domain);
  const dayTimetables = await getAllTimetablesForDay(domain, day);

  const availabilityMap = new Map<string, TeacherAvailabilityInfo>();

  // Standard period timings for reference
  const standardPeriods = [
    '08:00-08:45', '08:45-09:30', '09:30-10:15',
    '10:30-11:15', '11:15-12:00', '12:00-12:45',
    '13:30-14:15', '14:15-15:00'
  ];

  // Initialize all teachers with empty schedules
  teachers.forEach(teacher => {
    availabilityMap.set(teacher.teacherId, {
      teacherId: teacher.teacherId,
      teacherName: teacher.name,
      subject: teacher.subjects?.join(', ') || teacher.subject || '',
      busyPeriods: [],
      availablePeriods: [...standardPeriods],
    });
  });

  // Populate busy periods from existing timetables
  dayTimetables.forEach(timetable => {
    timetable.periods.forEach(period => {
      if (period.teacherId && !period.isBreak) {
        const teacherInfo = availabilityMap.get(period.teacherId);
        if (teacherInfo) {
          const timeSlot = `${period.startTime}-${period.endTime}`;
          teacherInfo.busyPeriods.push({
            time: timeSlot,
            className: timetable.className,
            subject: period.subject,
          });
          // Remove from available periods
          teacherInfo.availablePeriods = teacherInfo.availablePeriods.filter(
            t => t !== timeSlot
          );
        }
      }
    });
  });

  return availabilityMap;
};

// Get available teachers for a specific subject and time slot
export const getAvailableTeachersForSlot = async (
  domain: string,
  subject: string,
  day: string,
  timeSlot: string
): Promise<Teacher[]> => {
  const teachers = await fetchTeachersForTimetable(domain);
  const availabilityMap = await buildTeacherAvailabilityMap(domain, day);

  // Filter teachers who:
  // 1. Can teach the subject
  // 2. Are available at the given time slot
  const availableTeachers = teachers.filter(teacher => {
    const canTeachSubject =
      teacher.subject?.toLowerCase().includes(subject.toLowerCase()) ||
      teacher.subjects?.some(s => s.toLowerCase().includes(subject.toLowerCase()));

    const availability = availabilityMap.get(teacher.teacherId);
    const isAvailable = availability?.availablePeriods.includes(timeSlot) ?? true;

    return canTeachSubject && isAvailable;
  });

  return availableTeachers;
};

// Format teacher availability for AI context
export const formatTeacherAvailabilityForAI = async (
  domain: string,
  day: string
): Promise<string> => {
  const availabilityMap = await buildTeacherAvailabilityMap(domain, day);
  const teachers = await fetchTeachersForTimetable(domain);

  if (teachers.length === 0) return 'No teachers found.';

  let formatted = `\n\n📊 TEACHER AVAILABILITY FOR ${day.toUpperCase()}:\n\n`;
  formatted += `| Teacher | Subject(s) | Status | Busy Periods |\n`;
  formatted += `|---------|------------|--------|---------------|\n`;

  teachers.forEach(teacher => {
    const availability = availabilityMap.get(teacher.teacherId);
    const subjects = teacher.subjects?.join(', ') || teacher.subject || 'N/A';
    const busyCount = availability?.busyPeriods.length || 0;
    const status = busyCount === 0 ? '✅ Free' : busyCount >= 6 ? '🔴 Busy' : '🟡 Partial';

    const busyDetails = availability?.busyPeriods
      .map(b => `${b.time} (${b.className})`)
      .join('; ') || 'None';

    formatted += `| ${teacher.name} | ${subjects} | ${status} | ${busyDetails} |\n`;
  });

  // Add summary of available teachers per subject
  const subjectsSet = new Set<string>();
  teachers.forEach(t => {
    if (t.subject) subjectsSet.add(t.subject);
    t.subjects?.forEach(s => subjectsSet.add(s));
  });

  formatted += `\n\n📚 AVAILABLE TEACHERS BY SUBJECT:\n`;

  for (const subject of Array.from(subjectsSet).sort()) {
    const subjectTeachers = teachers.filter(t =>
      t.subject?.toLowerCase() === subject.toLowerCase() ||
      t.subjects?.some(s => s.toLowerCase() === subject.toLowerCase())
    );

    const freeTeachers = subjectTeachers.filter(t => {
      const avail = availabilityMap.get(t.teacherId);
      return (avail?.busyPeriods.length || 0) < 6;
    });

    formatted += `\n**${subject}:**\n`;
    if (freeTeachers.length > 0) {
      freeTeachers.forEach(t => {
        const avail = availabilityMap.get(t.teacherId);
        const freeSlots = avail?.availablePeriods.length || 8;
        formatted += `  - ${t.name} (${freeSlots} slots free)\n`;
      });
    } else {
      formatted += `  - ⚠️ No available teachers\n`;
    }
  }

  return formatted;
};

// Check if teacher is available at a specific time
export const isTeacherAvailable = async (
  domain: string,
  teacherId: string,
  day: string,
  startTime: string,
  endTime: string
): Promise<{ available: boolean; conflict?: string }> => {
  const schedule = await getTeacherScheduleForDay(domain, teacherId, day);

  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const requestStart = timeToMinutes(startTime);
  const requestEnd = timeToMinutes(endTime);

  for (const period of schedule) {
    const periodStart = timeToMinutes(period.startTime);
    const periodEnd = timeToMinutes(period.endTime);

    // Check for overlap
    if (requestStart < periodEnd && requestEnd > periodStart) {
      return {
        available: false,
        conflict: `Teacher is busy with ${period.subject} from ${period.startTime} to ${period.endTime}`,
      };
    }
  }

  return { available: true };
};

// Generate a suggested timetable based on subjects and available teachers
export const generateSuggestedTimetable = async (
  domain: string,
  request: TimetableGenerationRequest
): Promise<{ success: boolean; timetable?: TimetableConfig; errors?: string[] }> => {
  const errors: string[] = [];
  const teachers = await fetchTeachersForTimetable(domain);

  if (teachers.length === 0) {
    return { success: false, errors: ['No teachers found in the system'] };
  }

  const periods: Period[] = [];
  let periodOrder = 1;

  for (let i = 0; i < request.periodTimings.length; i++) {
    const timing = request.periodTimings[i];
    const subject = request.subjects[i] || 'Free Period';

    if (timing.isBreak) {
      periods.push({
        periodId: `period_${periodOrder}`,
        periodNumber: `Period ${periodOrder}`,
        order: periodOrder,
        subject: 'BREAK',
        teacherId: '',
        teacherName: '',
        startTime: timing.startTime,
        endTime: timing.endTime,
        duration: calculateDuration(timing.startTime, timing.endTime),
        isBreak: true,
        type: 'Break',
      });
    } else {
      // Find available teacher for this subject
      const subjectTeachers = teachers.filter(t =>
        t.subject?.toLowerCase().includes(subject.toLowerCase()) ||
        t.subjects?.some(s => s.toLowerCase().includes(subject.toLowerCase()))
      );

      let assignedTeacher: Teacher | null = null;

      for (const teacher of subjectTeachers) {
        const availability = await isTeacherAvailable(
          domain,
          teacher.teacherId,
          request.day,
          timing.startTime,
          timing.endTime
        );

        if (availability.available) {
          assignedTeacher = teacher;
          break;
        }
      }

      if (!assignedTeacher && subjectTeachers.length > 0) {
        errors.push(`No available teacher for ${subject} at ${timing.startTime}-${timing.endTime}. Using first available teacher.`);
        assignedTeacher = subjectTeachers[0];
      }

      if (!assignedTeacher) {
        errors.push(`No teacher found for subject: ${subject}`);
      }

      periods.push({
        periodId: `period_${periodOrder}`,
        periodNumber: `Period ${periodOrder}`,
        order: periodOrder,
        subject: subject,
        teacherId: assignedTeacher?.teacherId || '',
        teacherName: assignedTeacher?.name || 'TBA',
        startTime: timing.startTime,
        endTime: timing.endTime,
        duration: calculateDuration(timing.startTime, timing.endTime),
        isBreak: false,
        type: 'Regular',
      });
    }

    periodOrder++;
  }

  const timetable: TimetableConfig = {
    classId: `class${request.classId}${request.section.toLowerCase()}`,
    className: `Class ${request.classId}${request.section}`,
    section: request.section,
    day: request.day,
    academicYear: request.academicYear || '2025-26',
    periods,
  };

  return {
    success: errors.length === 0,
    timetable,
    errors: errors.length > 0 ? errors : undefined,
  };
};

// Save timetable to Firestore
export const saveTimetable = async (
  domain: string,
  timetable: TimetableConfig
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Use existing docId if available (for updates), otherwise generate new one
    const docId = timetable.docId || `${timetable.classId}_${timetable.day.toLowerCase()}`;
    const timetableRef = doc(db, `school-users/${domain}/timetables`, docId);

    // Remove docId from the data being saved (it's metadata, not part of timetable)
    const { docId: _, ...timetableData } = timetable;

    await setDoc(timetableRef, {
      ...timetableData,
      updatedAt: Timestamp.now(),
      updatedBy: 'ai_assistant',
    }, { merge: true }); // Use merge to preserve existing fields like createdAt

    console.log(`✅ Timetable saved/updated: ${docId}`);
    return { success: true };
  } catch (error: any) {
    console.error('Error saving timetable:', error);
    return { success: false, error: error.message };
  }
};

// Get all subjects taught by teachers
export const getAllSubjects = async (domain: string): Promise<string[]> => {
  const teachers = await fetchTeachersForTimetable(domain);
  const subjectsSet = new Set<string>();

  teachers.forEach(teacher => {
    if (teacher.subject) {
      subjectsSet.add(teacher.subject);
    }
    teacher.subjects?.forEach(s => subjectsSet.add(s));
  });

  return Array.from(subjectsSet).sort();
};

// Helper function to calculate duration
const calculateDuration = (startTime: string, endTime: string): number => {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  return (endHour * 60 + endMin) - (startHour * 60 + startMin);
};

// Format timetable for AI response
export const formatTimetableForAI = (timetable: TimetableConfig): string => {
  let formatted = `\n📅 Timetable for ${timetable.className} - ${timetable.day}\n`;
  formatted += `Academic Year: ${timetable.academicYear}\n\n`;
  formatted += `| # | Time | Subject | Teacher |\n`;
  formatted += `|---|------|---------|----------|\n`;

  timetable.periods.forEach((period, index) => {
    const timeStr = `${period.startTime}-${period.endTime}`;
    if (period.isBreak) {
      formatted += `| ${index + 1} | ${timeStr} | 🍽️ BREAK | - |\n`;
    } else {
      formatted += `| ${index + 1} | ${timeStr} | ${period.subject} | ${period.teacherName} |\n`;
    }
  });

  return formatted;
};

// Format teachers list for AI context
export const formatTeachersForAI = (teachers: Teacher[]): string => {
  if (teachers.length === 0) return 'No teachers found.';

  let formatted = `\n👨‍🏫 Available Teachers (${teachers.length}):\n\n`;
  formatted += `| Name | Subject(s) | Teacher ID |\n`;
  formatted += `|------|------------|------------|\n`;

  teachers.forEach(teacher => {
    const subjects = teacher.subjects?.join(', ') || teacher.subject || 'N/A';
    formatted += `| ${teacher.name} | ${subjects} | ${teacher.teacherId} |\n`;
  });

  return formatted;
};

// Build comprehensive context for AI timetable design
export const buildTimetableDesignContext = async (domain: string, day?: string): Promise<string> => {
  const teachers = await fetchTeachersForTimetable(domain);
  const subjects = await getAllSubjects(domain);

  let context = `\n\n🎓 TIMETABLE DESIGN CONTEXT:\n\n`;

  context += `📚 Available Subjects: ${subjects.join(', ')}\n\n`;
  context += formatTeachersForAI(teachers);

  context += `\n\n📋 Standard Period Timings (Suggested):\n`;
  context += `| Period | Time |\n`;
  context += `|--------|------|\n`;
  context += `| 1 | 08:00-08:45 |\n`;
  context += `| 2 | 08:45-09:30 |\n`;
  context += `| 3 | 09:30-10:15 |\n`;
  context += `| Break | 10:15-10:30 |\n`;
  context += `| 4 | 10:30-11:15 |\n`;
  context += `| 5 | 11:15-12:00 |\n`;
  context += `| 6 | 12:00-12:45 |\n`;
  context += `| Lunch | 12:45-13:30 |\n`;
  context += `| 7 | 13:30-14:15 |\n`;
  context += `| 8 | 14:15-15:00 |\n`;

  // Add teacher availability for the specified day
  if (day) {
    context += await formatTeacherAvailabilityForAI(domain, day);
  }

  context += `\n\n💡 To create a timetable, specify:\n`;
  context += `- Class and Section (e.g., Class 10A)\n`;
  context += `- Day of the week\n`;
  context += `- List of subjects for each period\n`;
  context += `- I will automatically assign available teachers!\n`;

  return context;
};

// Build context with availability for a specific day (called when user mentions a day)
export const buildTimetableContextWithAvailability = async (
  domain: string,
  day: string
): Promise<string> => {
  return buildTimetableDesignContext(domain, day);
};

// =====================================================
// EXCEL TEMPLATE GENERATION FUNCTIONS
// =====================================================

export interface ExcelTemplateOptions {
  className?: string;
  section?: string;
  includeTeachers?: boolean;
  periods?: number;
  days?: string[];
}

// Standard period timings
const STANDARD_PERIOD_TIMINGS = [
  { period: 1, start: '08:00', end: '08:45' },
  { period: 2, start: '08:45', end: '09:30' },
  { period: 3, start: '09:30', end: '10:15' },
  { period: 4, start: '10:15', end: '10:30', isBreak: true, label: 'Short Break' },
  { period: 5, start: '10:30', end: '11:15' },
  { period: 6, start: '11:15', end: '12:00' },
  { period: 7, start: '12:00', end: '12:45' },
  { period: 8, start: '12:45', end: '13:30', isBreak: true, label: 'Lunch Break' },
  { period: 9, start: '13:30', end: '14:15' },
  { period: 10, start: '14:15', end: '15:00' },
];

const DEFAULT_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Generate an Excel timetable template with actual teacher data from the school
 */
export const generateTimetableExcelTemplate = async (
  domain: string,
  options: ExcelTemplateOptions = {}
): Promise<Blob> => {
  const {
    className = '10',
    section = 'A',
    includeTeachers = true,
    periods = 8,
    days = DEFAULT_DAYS,
  } = options;

  // Fetch teachers from the school database
  const teachers = includeTeachers ? await fetchTeachersForTimetable(domain) : [];
  const subjects = await getAllSubjects(domain);

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Create main timetable sheet
  const timetableData: any[] = [];

  days.forEach(day => {
    const row: any = { Day: day };
    let periodIndex = 1;

    for (let i = 0; i < STANDARD_PERIOD_TIMINGS.length && periodIndex <= periods; i++) {
      const timing = STANDARD_PERIOD_TIMINGS[i];

      if (timing.isBreak) {
        row[`P${periodIndex} Subject`] = 'BREAK';
        row[`P${periodIndex} Teacher Name`] = '';
        row[`P${periodIndex} Teacher ID`] = '';
        row[`P${periodIndex} Time`] = `${timing.start}-${timing.end}`;
        row[`P${periodIndex} Type`] = 'Break';
      } else {
        // Assign a sample subject from available subjects
        const subjectIndex = (periodIndex - 1) % subjects.length;
        const sampleSubject = subjects[subjectIndex] || 'Subject TBD';

        // Find a teacher for this subject
        const subjectTeacher = teachers.find(
          t => t.subject?.toLowerCase() === sampleSubject.toLowerCase() ||
               t.subjects?.some(s => s.toLowerCase() === sampleSubject.toLowerCase())
        );

        row[`P${periodIndex} Subject`] = sampleSubject;
        row[`P${periodIndex} Teacher Name`] = subjectTeacher?.name || 'Teacher TBD';
        row[`P${periodIndex} Teacher ID`] = subjectTeacher?.teacherId || '';
        row[`P${periodIndex} Time`] = `${timing.start}-${timing.end}`;
        row[`P${periodIndex} Type`] = 'Regular';
      }

      periodIndex++;
    }

    timetableData.push(row);
  });

  const wsMain = XLSX.utils.json_to_sheet(timetableData);

  // Set column widths
  const colWidths = [{ wch: 12 }]; // Day column
  for (let i = 0; i < periods; i++) {
    colWidths.push({ wch: 20 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 });
  }
  wsMain['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(wb, wsMain, `Class${className}${section}`);

  // Create teachers reference sheet
  if (includeTeachers && teachers.length > 0) {
    const teacherData = teachers.map(t => ({
      'Teacher ID': t.teacherId,
      'Teacher Name': t.name,
      'Subject(s)': t.subjects?.join(', ') || t.subject || '',
      'Department': t.department || '',
      'Email': t.email || '',
    }));

    const wsTeachers = XLSX.utils.json_to_sheet(teacherData);
    wsTeachers['!cols'] = [
      { wch: 15 },
      { wch: 25 },
      { wch: 30 },
      { wch: 20 },
      { wch: 30 },
    ];
    XLSX.utils.book_append_sheet(wb, wsTeachers, 'Teachers Reference');
  }

  // Create subjects reference sheet
  if (subjects.length > 0) {
    const subjectData = subjects.map((s, i) => ({
      '#': i + 1,
      'Subject': s,
      'Teachers Available': teachers
        .filter(t => t.subject?.toLowerCase() === s.toLowerCase() ||
                    t.subjects?.some(sub => sub.toLowerCase() === s.toLowerCase()))
        .map(t => t.name)
        .join(', ') || 'No teacher assigned',
    }));

    const wsSubjects = XLSX.utils.json_to_sheet(subjectData);
    wsSubjects['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(wb, wsSubjects, 'Subjects Reference');
  }

  // Create instructions sheet
  const instructionsData = [
    { 'Instructions': 'HOW TO USE THIS TEMPLATE' },
    { 'Instructions': '=' .repeat(50) },
    { 'Instructions': '' },
    { 'Instructions': '1. TIMETABLE SHEET:' },
    { 'Instructions': '   - Each row represents a day of the week' },
    { 'Instructions': '   - Columns P1-P8 represent periods' },
    { 'Instructions': '   - For each period, fill in:' },
    { 'Instructions': '     * Subject: Name of the subject' },
    { 'Instructions': '     * Teacher Name: Full name of the teacher' },
    { 'Instructions': '     * Teacher ID: ID from Teachers Reference sheet' },
    { 'Instructions': '     * Time: Period timing (e.g., 08:00-08:45)' },
    { 'Instructions': '     * Type: Regular, Lab, Sports, Activity, or Break' },
    { 'Instructions': '' },
    { 'Instructions': '2. FOR BREAK PERIODS:' },
    { 'Instructions': '   - Set Subject to "BREAK"' },
    { 'Instructions': '   - Leave Teacher Name and Teacher ID empty' },
    { 'Instructions': '   - Set Type to "Break"' },
    { 'Instructions': '' },
    { 'Instructions': '3. TEACHERS REFERENCE:' },
    { 'Instructions': '   - Contains all teachers from your school database' },
    { 'Instructions': '   - Use Teacher IDs from this sheet' },
    { 'Instructions': '' },
    { 'Instructions': '4. SUBJECTS REFERENCE:' },
    { 'Instructions': '   - Shows available subjects and their teachers' },
    { 'Instructions': '' },
    { 'Instructions': '5. UPLOAD:' },
    { 'Instructions': '   - Go to SIS > Time Table > Upload' },
    { 'Instructions': '   - Select this file to upload' },
    { 'Instructions': '' },
    { 'Instructions': `Generated for: Class ${className}${section}` },
    { 'Instructions': `Generated on: ${new Date().toLocaleString()}` },
    { 'Instructions': `Total Teachers: ${teachers.length}` },
    { 'Instructions': `Total Subjects: ${subjects.length}` },
  ];

  const wsInstructions = XLSX.utils.json_to_sheet(instructionsData);
  wsInstructions['!cols'] = [{ wch: 60 }];
  XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

  // Convert to blob
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

/**
 * Generate bulk timetable template for multiple classes
 */
export const generateBulkTimetableExcelTemplate = async (
  domain: string,
  classes: { className: string; section: string }[] = [
    { className: '1', section: 'A' },
    { className: '1', section: 'B' },
    { className: '2', section: 'A' },
    { className: '2', section: 'B' },
  ]
): Promise<Blob> => {
  const teachers = await fetchTeachersForTimetable(domain);
  const subjects = await getAllSubjects(domain);

  const wb = XLSX.utils.book_new();
  const days = DEFAULT_DAYS;
  const periods = 8;

  // Create a sheet for each class
  for (const cls of classes) {
    const timetableData: any[] = [];

    days.forEach(day => {
      const row: any = { Day: day };
      let periodIndex = 1;

      for (let i = 0; i < STANDARD_PERIOD_TIMINGS.length && periodIndex <= periods; i++) {
        const timing = STANDARD_PERIOD_TIMINGS[i];

        if (timing.isBreak) {
          row[`P${periodIndex} Subject`] = 'BREAK';
          row[`P${periodIndex} Teacher Name`] = '';
          row[`P${periodIndex} Teacher ID`] = '';
          row[`P${periodIndex} Time`] = `${timing.start}-${timing.end}`;
          row[`P${periodIndex} Type`] = 'Break';
        } else {
          const subjectIndex = (periodIndex - 1) % subjects.length;
          const sampleSubject = subjects[subjectIndex] || 'Subject TBD';

          const subjectTeacher = teachers.find(
            t => t.subject?.toLowerCase() === sampleSubject.toLowerCase() ||
                 t.subjects?.some(s => s.toLowerCase() === sampleSubject.toLowerCase())
          );

          row[`P${periodIndex} Subject`] = sampleSubject;
          row[`P${periodIndex} Teacher Name`] = subjectTeacher?.name || 'Teacher TBD';
          row[`P${periodIndex} Teacher ID`] = subjectTeacher?.teacherId || '';
          row[`P${periodIndex} Time`] = `${timing.start}-${timing.end}`;
          row[`P${periodIndex} Type`] = 'Regular';
        }

        periodIndex++;
      }

      timetableData.push(row);
    });

    const ws = XLSX.utils.json_to_sheet(timetableData);
    const colWidths = [{ wch: 12 }];
    for (let i = 0; i < periods; i++) {
      colWidths.push({ wch: 20 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 });
    }
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, `Class${cls.className}${cls.section}`);
  }

  // Add teachers reference sheet
  if (teachers.length > 0) {
    const teacherData = teachers.map(t => ({
      'Teacher ID': t.teacherId,
      'Teacher Name': t.name,
      'Subject(s)': t.subjects?.join(', ') || t.subject || '',
      'Department': t.department || '',
    }));

    const wsTeachers = XLSX.utils.json_to_sheet(teacherData);
    wsTeachers['!cols'] = [{ wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, wsTeachers, 'Teachers Reference');
  }

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

/**
 * Helper function to download blob as file
 */
export const downloadExcelBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// =====================================================
// TIMETABLE MODIFICATION FUNCTIONS (AI Agent)
// =====================================================

export interface TimetableModification {
  type: 'update_period' | 'swap_periods' | 'change_teacher' | 'add_period' | 'remove_period';
  classId: string;
  day: string;
  periodNumber?: number;
  targetPeriodNumber?: number; // For swapping
  newSubject?: string;
  newTeacherId?: string;
  newTeacherName?: string;
  newStartTime?: string;
  newEndTime?: string;
}

export interface TimetableModificationResult {
  success: boolean;
  message: string;
  updatedTimetable?: TimetableConfig;
  conflictWarning?: string;
}

/**
 * Fetch an existing timetable for modification
 */
export const fetchTimetableForModification = async (
  domain: string,
  classId: string,
  day: string
): Promise<TimetableConfig | null> => {
  try {
    console.log(`🔍 Searching for timetable: classId=${classId}, day=${day}, domain=${domain}`);
    const timetablesRef = collection(db, `school-users/${domain}/timetables`);
    const snapshot = await getDocs(timetablesRef);

    let foundTimetable: TimetableConfig | null = null;

    snapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const docClassId = data.classId?.toLowerCase() || '';
      const docDay = data.day?.toLowerCase() || '';
      const searchClassId = classId.toLowerCase();
      const searchDay = day.toLowerCase();

      // Check for exact match or if document classId contains our search classId
      // e.g., search for "class10a" should match document with "class10a"
      const classMatches = docClassId === searchClassId ||
                          docClassId.startsWith(searchClassId) ||
                          searchClassId.startsWith(docClassId);
      const dayMatches = docDay === searchDay;

      console.log(`  📄 Doc: ${docSnapshot.id}, classId: ${docClassId}, day: ${docDay}, matches: ${classMatches && dayMatches}`);

      if (classMatches && dayMatches) {
        foundTimetable = {
          classId: data.classId,
          className: data.className,
          section: data.section || '',
          day: data.day,
          academicYear: data.academicYear || '2025-26',
          periods: data.periods || [],
          docId: docSnapshot.id, // Capture the document ID for updates
        };
        console.log(`✅ Found timetable: ${docSnapshot.id} for ${data.classId} on ${data.day}`);
      }
    });

    if (!foundTimetable) {
      console.log(`❌ No timetable found for classId=${classId}, day=${day}`);
    }

    return foundTimetable;
  } catch (error) {
    console.error('Error fetching timetable for modification:', error);
    return null;
  }
};

/**
 * Update a specific period in the timetable
 */
export const updateTimetablePeriod = async (
  domain: string,
  modification: TimetableModification
): Promise<TimetableModificationResult> => {
  try {
    const timetable = await fetchTimetableForModification(domain, modification.classId, modification.day);

    if (!timetable) {
      return {
        success: false,
        message: `Timetable not found for ${modification.classId} on ${modification.day}`,
      };
    }

    const periodIndex = timetable.periods.findIndex(
      p => p.order === modification.periodNumber || parseInt(p.periodNumber.replace(/\D/g, '')) === modification.periodNumber
    );

    if (periodIndex === -1) {
      return {
        success: false,
        message: `Period ${modification.periodNumber} not found in timetable`,
      };
    }

    // Check teacher availability if changing teacher
    let conflictWarning: string | undefined;
    if (modification.newTeacherId) {
      const availability = await isTeacherAvailable(
        domain,
        modification.newTeacherId,
        modification.day,
        timetable.periods[periodIndex].startTime,
        timetable.periods[periodIndex].endTime
      );

      if (!availability.available) {
        conflictWarning = `⚠️ Warning: ${availability.conflict}`;
      }
    }

    // Update the period
    const updatedPeriods = [...timetable.periods];
    updatedPeriods[periodIndex] = {
      ...updatedPeriods[periodIndex],
      subject: modification.newSubject || updatedPeriods[periodIndex].subject,
      teacherId: modification.newTeacherId || updatedPeriods[periodIndex].teacherId,
      teacherName: modification.newTeacherName || updatedPeriods[periodIndex].teacherName,
      startTime: modification.newStartTime || updatedPeriods[periodIndex].startTime,
      endTime: modification.newEndTime || updatedPeriods[periodIndex].endTime,
    };

    const updatedTimetable: TimetableConfig = {
      ...timetable,
      periods: updatedPeriods,
    };

    // Save the updated timetable
    const saveResult = await saveTimetable(domain, updatedTimetable);

    if (!saveResult.success) {
      return {
        success: false,
        message: `Failed to save: ${saveResult.error}`,
      };
    }

    // Update teacher schedule
    await updateTeacherScheduleAfterModification(domain, updatedTimetable, periodIndex);

    return {
      success: true,
      message: `✅ Period ${modification.periodNumber} updated successfully`,
      updatedTimetable,
      conflictWarning,
    };
  } catch (error: any) {
    console.error('Error updating timetable period:', error);
    return {
      success: false,
      message: `Error: ${error.message}`,
    };
  }
};

/**
 * Swap two periods in the timetable
 */
export const swapTimetablePeriods = async (
  domain: string,
  classId: string,
  day: string,
  period1: number,
  period2: number
): Promise<TimetableModificationResult> => {
  try {
    const timetable = await fetchTimetableForModification(domain, classId, day);

    if (!timetable) {
      return {
        success: false,
        message: `Timetable not found for ${classId} on ${day}`,
      };
    }

    const idx1 = timetable.periods.findIndex(p => p.order === period1);
    const idx2 = timetable.periods.findIndex(p => p.order === period2);

    if (idx1 === -1 || idx2 === -1) {
      return {
        success: false,
        message: `One or both periods not found (${period1}, ${period2})`,
      };
    }

    // Check if either is a break
    if (timetable.periods[idx1].isBreak || timetable.periods[idx2].isBreak) {
      return {
        success: false,
        message: `Cannot swap break periods`,
      };
    }

    // Swap the content but keep time slots
    const updatedPeriods = [...timetable.periods];
    const temp = {
      subject: updatedPeriods[idx1].subject,
      teacherId: updatedPeriods[idx1].teacherId,
      teacherName: updatedPeriods[idx1].teacherName,
    };

    updatedPeriods[idx1] = {
      ...updatedPeriods[idx1],
      subject: updatedPeriods[idx2].subject,
      teacherId: updatedPeriods[idx2].teacherId,
      teacherName: updatedPeriods[idx2].teacherName,
    };

    updatedPeriods[idx2] = {
      ...updatedPeriods[idx2],
      subject: temp.subject,
      teacherId: temp.teacherId,
      teacherName: temp.teacherName,
    };

    const updatedTimetable: TimetableConfig = {
      ...timetable,
      periods: updatedPeriods,
    };

    const saveResult = await saveTimetable(domain, updatedTimetable);

    if (!saveResult.success) {
      return {
        success: false,
        message: `Failed to save: ${saveResult.error}`,
      };
    }

    return {
      success: true,
      message: `✅ Swapped Period ${period1} (${updatedPeriods[idx2].subject}) with Period ${period2} (${updatedPeriods[idx1].subject})`,
      updatedTimetable,
    };
  } catch (error: any) {
    console.error('Error swapping periods:', error);
    return {
      success: false,
      message: `Error: ${error.message}`,
    };
  }
};

/**
 * Change teacher for a specific period
 */
export const changeTeacherForPeriod = async (
  domain: string,
  classId: string,
  day: string,
  periodNumber: number,
  newTeacherId: string
): Promise<TimetableModificationResult> => {
  const teachers = await fetchTeachersForTimetable(domain);
  const teacher = teachers.find(t => t.teacherId === newTeacherId);

  if (!teacher) {
    return {
      success: false,
      message: `Teacher with ID ${newTeacherId} not found`,
    };
  }

  return updateTimetablePeriod(domain, {
    type: 'change_teacher',
    classId,
    day,
    periodNumber,
    newTeacherId: teacher.teacherId,
    newTeacherName: teacher.name,
  });
};

/**
 * Update teacher schedule after timetable modification
 */
const updateTeacherScheduleAfterModification = async (
  domain: string,
  timetable: TimetableConfig,
  modifiedPeriodIndex: number
): Promise<void> => {
  try {
    const period = timetable.periods[modifiedPeriodIndex];
    if (!period.teacherId || period.isBreak) return;

    const scheduleKey = `${period.teacherId}_${timetable.day}`;
    const scheduleRef = doc(db, `school-users/${domain}/teacher-schedules`, scheduleKey);

    // Get all periods for this teacher on this day from all timetables
    const allTimetables = await getAllTimetablesForDay(domain, timetable.day);
    const teacherPeriods: any[] = [];

    allTimetables.forEach(tt => {
      tt.periods.forEach(p => {
        if (p.teacherId === period.teacherId && !p.isBreak) {
          teacherPeriods.push({
            ...p,
            className: tt.className,
          });
        }
      });
    });

    // Also add the current modified period
    const existingPeriodIdx = teacherPeriods.findIndex(
      p => p.startTime === period.startTime && p.className === timetable.className
    );
    if (existingPeriodIdx === -1) {
      teacherPeriods.push({
        ...period,
        className: timetable.className,
      });
    }

    // Sort by start time
    teacherPeriods.sort((a, b) => {
      const timeA = a.startTime?.split(':').map(Number) || [0, 0];
      const timeB = b.startTime?.split(':').map(Number) || [0, 0];
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });

    await setDoc(scheduleRef, {
      teacherId: period.teacherId,
      teacherName: period.teacherName,
      day: timetable.day,
      periods: teacherPeriods,
      lastUpdated: Timestamp.now(),
    }, { merge: true });

    console.log(`✅ Updated teacher schedule for ${period.teacherName}`);
  } catch (error) {
    console.error('Error updating teacher schedule:', error);
  }
};

/**
 * Get list of available teachers for a subject at a specific time
 */
export const getAvailableTeachersForPeriod = async (
  domain: string,
  day: string,
  startTime: string,
  endTime: string,
  subject?: string
): Promise<{ teacher: Teacher; available: boolean; conflict?: string }[]> => {
  const teachers = await fetchTeachersForTimetable(domain);
  const results: { teacher: Teacher; available: boolean; conflict?: string }[] = [];

  for (const teacher of teachers) {
    // If subject specified, filter by subject
    if (subject) {
      const canTeach =
        teacher.subject?.toLowerCase().includes(subject.toLowerCase()) ||
        teacher.subjects?.some(s => s.toLowerCase().includes(subject.toLowerCase()));
      if (!canTeach) continue;
    }

    const availability = await isTeacherAvailable(domain, teacher.teacherId, day, startTime, endTime);
    results.push({
      teacher,
      available: availability.available,
      conflict: availability.conflict,
    });
  }

  // Sort: available teachers first
  return results.sort((a, b) => (a.available === b.available ? 0 : a.available ? -1 : 1));
};

/**
 * Format modification result for AI response
 */
export const formatModificationResultForAI = (result: TimetableModificationResult): string => {
  let response = result.success ? '✅ **Modification Successful**\n\n' : '❌ **Modification Failed**\n\n';
  response += result.message + '\n';

  if (result.conflictWarning) {
    response += `\n${result.conflictWarning}\n`;
  }

  if (result.updatedTimetable) {
    response += '\n**Updated Timetable:**\n';
    response += formatTimetableForAI(result.updatedTimetable);
  }

  return response;
};

/**
 * Parse AI command for timetable modification
 * Returns structured modification request or null if not a modification command
 */
export const parseModificationCommand = (
  message: string
): { action: string; details: Record<string, any> } | null => {
  const lowerMessage = message.toLowerCase();

  // Detect swap command
  const swapMatch = lowerMessage.match(/swap\s+period\s*(\d+)\s*(?:and|with|&)\s*period\s*(\d+)/i);
  if (swapMatch) {
    return {
      action: 'swap',
      details: {
        period1: parseInt(swapMatch[1]),
        period2: parseInt(swapMatch[2]),
      },
    };
  }

  // Detect change teacher command
  const changeTeacherMatch = lowerMessage.match(/(?:change|assign|set)\s+(?:teacher|instructor)\s+(?:for\s+)?period\s*(\d+)\s+to\s+(.+)/i);
  if (changeTeacherMatch) {
    return {
      action: 'change_teacher',
      details: {
        periodNumber: parseInt(changeTeacherMatch[1]),
        teacherNameOrId: changeTeacherMatch[2].trim(),
      },
    };
  }

  // Detect update subject command
  const updateSubjectMatch = lowerMessage.match(/(?:change|update|set)\s+(?:subject|class)\s+(?:for\s+)?period\s*(\d+)\s+to\s+(.+)/i);
  if (updateSubjectMatch) {
    return {
      action: 'update_subject',
      details: {
        periodNumber: parseInt(updateSubjectMatch[1]),
        newSubject: updateSubjectMatch[2].trim(),
      },
    };
  }

  // Detect reschedule command
  const rescheduleMatch = lowerMessage.match(/(?:reschedule|move)\s+(.+?)\s+(?:from\s+)?period\s*(\d+)\s+to\s+period\s*(\d+)/i);
  if (rescheduleMatch) {
    return {
      action: 'reschedule',
      details: {
        subject: rescheduleMatch[1].trim(),
        fromPeriod: parseInt(rescheduleMatch[2]),
        toPeriod: parseInt(rescheduleMatch[3]),
      },
    };
  }

  // Detect show/view timetable command
  const showMatch = lowerMessage.match(/(?:show|view|display|get)\s+(?:the\s+)?(?:current\s+)?timetable\s+(?:for\s+)?(?:class\s+)?(\w+)\s*(\w*)\s*(?:on\s+)?(\w+)?/i);
  if (showMatch) {
    return {
      action: 'show_timetable',
      details: {
        classId: showMatch[1],
        section: showMatch[2] || '',
        day: showMatch[3] || '',
      },
    };
  }

  return null;
};

/**
 * Get all timetables for a class (all days)
 */
export const getAllTimetablesForClass = async (
  domain: string,
  classId: string
): Promise<TimetableConfig[]> => {
  try {
    const timetablesRef = collection(db, `school-users/${domain}/timetables`);
    const snapshot = await getDocs(timetablesRef);

    const timetables: TimetableConfig[] = [];

    snapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const docClassId = data.classId?.toLowerCase() || '';

      if (docClassId.includes(classId.toLowerCase())) {
        timetables.push({
          classId: data.classId,
          className: data.className,
          section: data.section || '',
          day: data.day,
          academicYear: data.academicYear || '2025-26',
          periods: data.periods || [],
          docId: docSnapshot.id, // Include document ID
        });
      }
    });

    // Sort by day of week
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return timetables.sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));
  } catch (error) {
    console.error('Error fetching all timetables for class:', error);
    return [];
  }
};

/**
 * Format all timetables for a class for AI display
 */
export const formatClassTimetablesForAI = (timetables: TimetableConfig[]): string => {
  if (timetables.length === 0) return 'No timetables found for this class.';

  let formatted = `\n📅 **Timetables for ${timetables[0].className}**\n`;
  formatted += `Academic Year: ${timetables[0].academicYear}\n\n`;

  timetables.forEach(tt => {
    formatted += `**${tt.day}:**\n`;
    formatted += `| # | Time | Subject | Teacher |\n`;
    formatted += `|---|------|---------|----------|\n`;

    tt.periods.forEach((period, idx) => {
      const timeStr = `${period.startTime}-${period.endTime}`;
      if (period.isBreak) {
        formatted += `| ${idx + 1} | ${timeStr} | 🍽️ BREAK | - |\n`;
      } else {
        formatted += `| ${idx + 1} | ${timeStr} | ${period.subject} | ${period.teacherName} |\n`;
      }
    });
    formatted += '\n';
  });

  return formatted;
};
